import { Link } from 'react-router-dom';
import { LogoMark } from '../components/Logo';
import './Privacy.css';

// Pre-launch privacy policy. Scope is deliberately small: at this stage the only
// personal data the public site handles is the waitlist email (+ which form it
// came from). Written for UK GDPR / PECR; controller is a sole trader.
export default function Privacy() {
  return (
    <div className="pp">
      <header className="pp-nav">
        <div className="pp-nav-in">
          <Link to="/" className="pp-logo" aria-label="Groundwork home">
            <LogoMark height={22} />
          </Link>
          <Link to="/" className="pp-back">Back to site</Link>
        </div>
      </header>

      <main className="pp-main">
        <p className="pp-eyebrow">Legal</p>
        <h1>Privacy Policy</h1>
        <p className="pp-updated">Last updated: 7 October 2026</p>

        <p>
          This policy explains what personal information Groundwork collects, why we collect it, and
          what we do with it. Groundwork is a football management game that is currently in
          development. At this stage, the only personal information we handle is the email address
          you give us if you choose to join our waitlist.
        </p>

        <h2>Who we are</h2>
        <p>
          Groundwork is run by a sole trader based in the United Kingdom, who is the data controller
          responsible for your personal information. If you have any questions about this policy or
          about your data, you can reach us at{' '}
          <a href="mailto:info@groundworkfc.com">info@groundworkfc.com</a>.
        </p>

        <h2>What we collect</h2>
        <p>
          When you join the waitlist, we collect the email address you enter. We also record which
          sign-up form you used (for example, the one at the top of the page or the one further
          down), so we understand where sign-ups come from. We do not ask for your name or any other
          details. As with most websites, our hosting providers may automatically process limited
          technical information such as your IP address to keep the site secure and running, but we
          do not use this to identify you.
        </p>

        <h2>Why we use it, and our legal basis</h2>
        <p>
          We use your email address for one purpose only: to let you know when Groundwork launches,
          and to share occasional important news about that launch. Our legal basis for this is your
          consent, which you give when you submit the waitlist form. We will never sell your email
          address, and we will not use it for anything other than launch-related updates.
        </p>

        <h2>Cookies and tracking</h2>
        <p>
          Our public website does not use advertising cookies, analytics, or any third-party
          tracking. If you create a Groundwork account and log in, we store a secure sign-in token in
          your browser. This is strictly necessary to keep you logged in and is not used to track
          you.
        </p>

        <h2>Who else is involved</h2>
        <p>
          We rely on a small number of trusted services to run Groundwork, and your data may be
          processed by them on our behalf: Supabase (which stores the waitlist and powers accounts),
          Netlify (which hosts the website), and Google Workspace (which provides our email). Each of
          these handles data under its own data protection commitments. We do not share your
          information with anyone else, and we do not sell it.
        </p>

        <h2>How long we keep it</h2>
        <p>
          We keep your email address on the waitlist until Groundwork launches and you have had the
          chance to create an account, or until you ask us to remove it, whichever comes first. If
          you ask us to delete it, we will do so promptly.
        </p>

        <h2>Your rights</h2>
        <p>
          Under UK data protection law you have the right to ask us for a copy of the personal data
          we hold about you, to have it corrected or deleted, to withdraw your consent at any time,
          and to object to or restrict how we use it. To exercise any of these rights, just email{' '}
          <a href="mailto:info@groundworkfc.com">info@groundworkfc.com</a> and we will take care of
          it. If you are unhappy with how we have handled your data, you also have the right to
          complain to the Information Commissioner{'’'}s Office (ICO), the UK{'’'}s data
          protection regulator, at{' '}
          <a href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a>.
        </p>

        <h2>Unsubscribing</h2>
        <p>
          You can unsubscribe from our emails at any time. Any email we send will include an
          unsubscribe link, and you can also simply email{' '}
          <a href="mailto:info@groundworkfc.com">info@groundworkfc.com</a> to be removed from the
          waitlist.
        </p>

        <h2>Changes to this policy</h2>
        <p>
          As Groundwork grows, we may update this policy. When we do, we will change the date at the
          top of this page. If we make a significant change that affects how we use your data, we
          will let waitlist members know by email.
        </p>

        <p className="pp-contact">
          Questions about privacy or your data? Email us at{' '}
          <a href="mailto:info@groundworkfc.com">info@groundworkfc.com</a>.
        </p>
      </main>

      <footer className="pp-foot">
        <span>&copy; 2026 Groundwork</span>
        <Link to="/">Back to site</Link>
      </footer>
    </div>
  );
}
